import React from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './ServiceList.css';
import resolveImageUrl from '../utils/imageResolver';

/**
 * ServiceList component for displaying service items with images, features, pricing, and links
 * 
 * Props:
 * - title: string (optional) - Section title
 * - titleDescription: string (optional) - Description below title
 * - services: array - Array of service objects with:
 *   - imageUrl: string (required) - Image URL
 *   - title: string (required) - Service title
 *   - features: array of strings (required) - List of features
 *   - cost: number (optional) - Price/cost
 *   - frequency: string (optional) - "annually", "monthly", "daily"
 *   - redirectURL: string (optional) - URL to redirect when clicked
 * - backgroundColor: string (optional) - Background color ("white" or "gray")
 * - body: string (optional) - Additional text below services
 */
const ServiceList = ({ 
  title, 
  titleDescription, 
  services = [], 
  backgroundColor = 'white',
  body 
}) => {
  const getFrequencyLabel = (frequency) => {
    const labels = {
      'annually': '年',
      'monthly': '月',
      'daily': '日'
    };
    return labels[frequency] || '';
  };

  const formatCost = (cost, frequency) => {
    if (cost === undefined || cost === null) return null;
    const frequencyLabel = frequency ? `/${getFrequencyLabel(frequency)}` : '';
    return `${cost.toFixed(2)}元${frequencyLabel}起`;
  };

  const ServiceItem = ({ service, index }) => {
    const content = (
      <div className="service-item">
        {service.imageUrl && (
          <div className="service-image">
            <img
              src={resolveImageUrl(service.imageUrl, {
                width: 400,
                height: 300,
                text: service.title,
                keywords: service.features || []
              })}
              alt={service.title || ''}
              className="service-img"
            />
          </div>
        )}
        <div className="service-content">
          <h3 className="service-title">{service.title}</h3>
          {service.features && Array.isArray(service.features) && service.features.length > 0 && (
            <ul className="service-features">
              {service.features.map((feature, idx) => (
                <li key={idx} className="service-feature-item">
                  {feature}
                </li>
              ))}
            </ul>
          )}
          {service.cost !== undefined && service.cost !== null && (
            <div className="service-cost">
              {formatCost(service.cost, service.frequency)}
            </div>
          )}
          {service.redirectURL && (
            <div className="service-action">
              <span className="service-link-text">了解详情</span>
              <span className="service-link-arrow">→</span>
            </div>
          )}
        </div>
      </div>
    );

    if (service.redirectURL) {
      return (
        <Link 
          to={service.redirectURL} 
          className="service-link"
        >
          {content}
        </Link>
      );
    }

    return content;
  };

  return (
    <div className={`service-list ${backgroundColor === 'white' ? 'bg-white' : 'bg-gray'}`}>
      <div className="container">
        {title && <h2 className="text-center service-list-title">{title}</h2>}
        {titleDescription && <p className="text-center text-muted service-list-title-description">{titleDescription}</p>}
        <div className="row service-list-items">
          {services.map((service, index) => (
            <div key={index} className="col-12 mb-4">
              <ServiceItem service={service} index={index} />
            </div>
          ))}
        </div>
        {body && <p className="text-center mt-4 service-list-body">{body}</p>}
      </div>
    </div>
  );
};

export default ServiceList;

